import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Product } from '../types/Product';

interface ProductCardProps {
  product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  return (
    <motion.div
      className="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col h-full"
      whileHover={{ y: -5, boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="relative overflow-hidden h-48">
        <img 
          src={product.image} 
          alt={product.name}
          className="w-full h-full object-cover object-center transition-transform duration-300 ease-in-out hover:scale-110"
        />
        <div className="absolute top-0 right-0 mt-2 mr-2 bg-gradient-to-r from-indigo-600 to-blue-500 text-white px-2 py-1 rounded-full text-xs font-bold">
          {product.origin} → {product.destination.length > 0 ? `${product.destination[0]}${product.destination.length > 1 ? ' +' + (product.destination.length - 1) : ''}` : ''}
        </div>
      </div>
      <div className="p-4 flex-grow">
        <div className="flex justify-between items-start">
          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full font-medium">{product.category}</span>
          <div className="flex items-center">
            <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span className="ml-1 text-sm font-medium text-gray-700">{product.rating.toFixed(1)}</span>
            <span className="ml-1 text-xs text-gray-500">({product.reviewCount})</span>
          </div>
        </div>
        <h3 className="mt-2 text-lg font-bold text-gray-900 line-clamp-2">{product.name}</h3>
        <p className="mt-1 text-sm text-gray-500 line-clamp-2">{product.description}</p>
        <div className="mt-3 flex justify-between items-center">
          <span className="text-xl font-bold text-indigo-700">
            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: product.currency }).format(product.price)}
          </span>
          <div className="text-sm text-gray-500">
            MOQ: {product.moq} unités
          </div>
        </div>
        <div className="mt-2 flex items-center text-xs text-gray-500">
          <span className="flex items-center">
            <span className="w-2 h-2 rounded-full bg-green-500 mr-1"></span>
            {product.stock > 0 ? 'En stock' : 'Rupture de stock'}
          </span>
          <span className="mx-2">•</span>
          <span>Vendeur: {product.seller.name}</span>
        </div>
      </div>
      <div className="px-4 pb-4 mt-auto">
        <Link to={`/produit/${product.id}`}>
          <motion.button
            className="w-full bg-gradient-to-r from-indigo-600 to-blue-500 text-white py-2 rounded-md font-medium"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Voir les détails
          </motion.button>
        </Link>
      </div>
    </motion.div>
  );
};

export default ProductCard;
